document.addEventListener('DOMContentLoaded', () => {
  const waitForSdk = () =>
    new Promise((resolve) => {
      if (window.gbbSdk) { resolve(window.gbbSdk); return; }
      document.addEventListener('gbb-sdk:loaded', (e) => resolve(e.detail?.sdk || window.gbbSdk), { once: true });
    });

  let pendingOpen = false;
  const earlyClickHandler = () => { pendingOpen = true; };
  for (const btn of document.querySelectorAll('.js-open-byob-drawer')) {
    btn.addEventListener('click', earlyClickHandler);
  }

  const byobBoot = (sdk, instance, PREFILL_VARIANT_ID) => {
    const steps = instance.f.getAllStepsData();
    const STEP_ID = steps[0]?.stepId;
    const CAT_ID = Object.keys(steps[0]?.categories || {})[0];

    // ─── Bundle price ─────────────────────────────────────────────────────────

    const bundleConfig = instance.f.getBundleConfig();
    const discountRule = bundleConfig?.discountConfiguration?.rules?.[0];
    const bundleFixedPrice =
      bundleConfig?.discountConfiguration?.discountMode === 'FIXED_BUNDLE_PRICE' && discountRule?.discountValue
        ? parseFloat(discountRule.discountValue) * 100
        : 0;

    // ─── DOM refs ─────────────────────────────────────────────────────────────

    const cardTemplate = document.getElementById('byobProductCardTemplate');
    const drawer = document.getElementById('byobDrawer');
    const overlay = document.getElementById('byobOverlay');
    const openTriggers = document.querySelectorAll('.js-open-byob-drawer');
    const scentGrid = document.getElementById('byobScentGrid');
    const slot1 = document.getElementById('byobSlot1');
    const slot2 = document.getElementById('byobSlot2');
    const footerSummary = document.getElementById('byobFooterSummary');
    const addToCartBtn = document.getElementById('byobAddToCartBtn');
    const totalPriceEl = document.getElementById('byobTotalPrice');
    const btnLabel = document.getElementById('byobBtnLabel');
    const fullPopup = document.getElementById('byobFullPopup');
    const drawerBody = document.getElementById('byobDrawerBody');
    const slotsContainer = document.querySelector('.byob-slots-container');
    const slotsFloat = document.getElementById('byobSlotsFloat');
    const floatImgScent1 = document.getElementById('byobFloatImgScent1');
    const floatImgScent2 = document.getElementById('byobFloatImgScent2');
    const collapseBtn = document.getElementById('byobSlotsCollapseBtn');
    const expandBtn = document.getElementById('byobSlotsExpandBtn');
    const errorPopup = document.getElementById('byob-error-popup');
    const errorPopupMessage = document.getElementById('byob-error-popup-message');
    let manuallyCollapsed = false;
    let isCheckingOut = false;

    // ─── Popups ───────────────────────────────────────────────────────────────

    let popupTimeout;
    const showFullPopup = () => {
      fullPopup.classList.add('is-visible');
      clearTimeout(popupTimeout);
      popupTimeout = setTimeout(hideFullPopup, 3000);
    };
    const hideFullPopup = () => fullPopup.classList.remove('is-visible');

    let errorTimeout;
    const showByobError = (msg) => {
      if (!errorPopup || !errorPopupMessage) return;
      errorPopupMessage.textContent = msg;
      errorPopup.classList.remove('hide');
      clearTimeout(errorTimeout);
      errorTimeout = setTimeout(() => errorPopup.classList.add('hide'), 3000);
    };

    // ─── Floating slot images ─────────────────────────────────────────────────

    const updateSlotsFloat = (scentItems) => {
      const setImg = (el, item) => {
        if (item) {
          el.src = item.image || '';
          el.alt = item.title || '';
          el.classList.add('is-filled');
        } else {
          el.src = '';
          el.alt = '';
          el.classList.remove('is-filled');
        }
      };
      setImg(floatImgScent1, scentItems[0] || null);
      setImg(floatImgScent2, scentItems[1] || null);
    };

    // ─── UI refresh ───────────────────────────────────────────────────────────

    const refreshUI = () => {
      const scentItems = instance.f.getStepCartItems(STEP_ID);

      [slot1, slot2].forEach((slot, i) => {
        const item = scentItems[i] || null;
        const img = slot.querySelector('img');
        if (item) {
          slot.classList.add('is-filled');
          img.src = item.image || '';
        } else {
          slot.classList.remove('is-filled');
          img.src = '';
        }
      });

      totalPriceEl.textContent = instance.f.formatPrice(bundleFixedPrice);

      if (scentItems.length === 0) {
        footerSummary.innerHTML = '<span>SELECT YOUR SCENTS</span>';
      } else {
        const imgs = scentItems.map((i) => `<img src="${i.image}" alt="${i.title}">`).join('');
        const titles = `<span class="byob-bundle-summary__titles">${scentItems.map((i) => i.title).join(' + ')}</span>`;
        footerSummary.innerHTML = `<div class="byob-bundle-summary__images">${imgs}</div>${titles}`;
      }

      const v = instance.f.validateBeforeAddBundleToCart({ showToast: false });
      addToCartBtn.disabled = !v.ok;

      const selectedIds = new Set(scentItems.map((i) => String(i.variant_id)));
      for (const el of scentGrid.querySelectorAll('.byob-product-item')) {
        el.classList.toggle('is-selected', selectedIds.has(el.dataset.variantId));
      }

      updateSlotsFloat(scentItems);
    };

    // ─── Category data helpers ────────────────────────────────────────────────

    const getCatData = (catId) => {
      if (instance.category?.f?.getData) return instance.category.f.getData(catId);
      for (const step of instance.f.getAllStepsData()) {
        if (step.categories?.[catId]) return step.categories[catId];
      }
      return null;
    };

    const getCatProducts = (catId) => {
      if (instance.category?.f?.getProducts) return instance.category.f.getProducts(catId);
      return getCatData(catId)?.products || [];
    };

    // ─── Product card builder ─────────────────────────────────────────────────

    const buildCard = (product) => {
      const variant = product.variants?.[0];
      if (!variant) return null;

      const variantGid = variant.variantId || variant.id;
      const variantNumId = String(variant.variant_id || '');
      const title = product.title || '';

      const priceDetails = instance.f.getVariantPriceDetails({ variantId: variantGid, categoryId: CAT_ID });
      const price = priceDetails?.price ?? variant.price ?? 0;
      const compareAt = Math.max(priceDetails?.compareAtPrice || 0, priceDetails?.originalPrice || 0);

      const el = cardTemplate.content.cloneNode(true).querySelector('.byob-product-item');
      el.dataset.variantId = variantNumId;

      const cardImg = el.querySelector('img');
      cardImg.src = product.featured_image || '';
      cardImg.alt = title;

      el.querySelector('.product-item__title').textContent = title;

      const compareEl = el.querySelector('.product-item__price-compare');
      compareEl.classList.toggle('is-hidden', compareAt <= price);
      compareEl.textContent = instance.f.formatPrice(compareAt);
      el.querySelector('.product-item__price-current').textContent = instance.f.formatPrice(price);

      const addBtn = el.querySelector('.byob-product-add');
      addBtn.setAttribute('aria-label', `Add ${title}`);
      addBtn.addEventListener('click', async () => {
        if (isCheckingOut) return;
        const scentItems = instance.f.getStepCartItems(STEP_ID);
        if (scentItems.length >= 2) { showFullPopup(); return; }

        const res = await instance.f.addProductToBundle({ stepId: STEP_ID, categoryId: CAT_ID, variantId: variantGid });
        if (!res.ok) {
          showByobError(
            res.data?.code === 'INVENTORY_VALIDATION_FAILED'
              ? 'This item is out of stock.'
              : res.data?.message || 'Could not add item. Please try again.'
          );
        }
      });

      return el;
    };

    // ─── Grid rendering ───────────────────────────────────────────────────────

    const renderScentGrid = () => {
      scentGrid.innerHTML = '';
      const cat = getCatData(CAT_ID);
      if (!cat || !cat.initialDataReady) {
        scentGrid.innerHTML = '<p class="byob-loading">Loading&hellip;</p>';
        return;
      }
      for (const p of getCatProducts(CAT_ID)) {
        const card = buildCard(p);
        if (card) scentGrid.appendChild(card);
      }
    };

    // ─── SDK subscriptions ────────────────────────────────────────────────────

    sdk.subscribe(sdk.events.CART_ITEM_UPDATED, refreshUI);
    sdk.subscribe(sdk.events.CATEGORY_PRODUCTS_FETCHED, (e) => {
      if (e.detail?.categoryId === CAT_ID) renderScentGrid();
    });

    // ─── Drawer scroll observer ───────────────────────────────────────────────

    const slotsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && entry.boundingClientRect.top < entry.rootBounds.top) {
            slotsFloat.classList.add('is-visible');
          } else if (entry.isIntersecting && !manuallyCollapsed) {
            slotsFloat.classList.remove('is-visible');
            slotsContainer.classList.remove('is-collapsed');
          }
        });
      },
      { root: drawerBody, threshold: 0 }
    );

    // ─── Drawer open / close ──────────────────────────────────────────────────

    const openDrawer = async () => {
      const scentItems = instance.f.getStepCartItems(STEP_ID);
      if (scentItems.length === 0 && PREFILL_VARIANT_ID) {
        await instance.f.addProductToBundle({
          stepId: STEP_ID,
          categoryId: CAT_ID,
          variantId: PREFILL_VARIANT_ID,
        });
      }
      refreshUI();
      manuallyCollapsed = false;
      slotsFloat.classList.remove('is-visible');
      slotsContainer.classList.remove('is-collapsed');
      drawer.classList.add('is-open');
      overlay.classList.add('is-visible');
      document.body.classList.add('byob-drawer-open');
      slotsObserver.observe(slotsContainer);
    };

    const closeDrawer = () => {
      slotsObserver.unobserve(slotsContainer);
      drawerBody.scrollTop = 0;
      manuallyCollapsed = false;
      slotsFloat.classList.remove('is-visible');
      slotsContainer.classList.remove('is-collapsed');
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-visible');
      document.body.classList.remove('byob-drawer-open');
      hideFullPopup();
    };

    for (const btn of openTriggers) {
      btn.removeEventListener('click', earlyClickHandler);
      btn.addEventListener('click', openDrawer);
    }
    if (pendingOpen) { pendingOpen = false; openDrawer(); }
    overlay.addEventListener('click', closeDrawer);
    document.getElementById('byobDrawerClose').addEventListener('click', closeDrawer);

    // ─── Slot remove (drawer stays open — no step navigation) ────────────────

    for (const btn of document.querySelectorAll('.byob-slot-remove')) {
      btn.addEventListener('click', async (e) => {
        if (isCheckingOut) return;
        e.stopPropagation();
        const slot = btn.dataset.slot;
        const scentItems = instance.f.getStepCartItems(STEP_ID);
        const item = scentItems[{ scent1: 0, scent2: 1 }[slot]];
        if (!item) return;
        const key = item.properties?._uniqueGbbSdkItemKey;
        if (key) await instance.f.updateCartItem(key, { updateQuantityBy: -item.quantity });
        hideFullPopup();
      });
    }

    // ─── Checkout ─────────────────────────────────────────────────────────────

    let gokwikModalClosedHandler = null;

    const removeBundleItemsAddedSince = async (snapshotKeys) => {
      const cartAfter = await fetch('/cart.js').then((r) => r.json());
      const updates = {};
      for (const item of cartAfter.items) {
        if (!snapshotKeys.has(item.key)) updates[item.key] = 0;
      }
      if (Object.keys(updates).length > 0) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });
      }
    };

    addToCartBtn.addEventListener('click', async () => {
      if (addToCartBtn.disabled) return;
      addToCartBtn.disabled = true;
      isCheckingOut = true;
      btnLabel.textContent = 'PROCESSING...';

      const cartBefore = await fetch('/cart.js').then((r) => r.json());
      const snapshotKeys = new Set(cartBefore.items.map((i) => i.key));
      const preExistingItems = cartBefore.items.map((i) => ({ id: i.id, quantity: i.quantity }));

      const res = await instance.f.addBundleToCart({ showToast: false });
      if (res.ok) {
        isCheckingOut = false;
        btnLabel.textContent = 'CHECKOUT |';
        closeDrawer();
        const gokwikBtn = document.querySelector('#byobGokwikTrigger .gokwik-checkout button');
        if (gokwikBtn) {
          if (preExistingItems.length > 0) {
            const removeUpdates = {};
            for (const i of cartBefore.items) { removeUpdates[i.key] = 0; }
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: removeUpdates }),
            });
          }
          if (gokwikModalClosedHandler) window.gokwikSdk?.off?.('modal_closed', gokwikModalClosedHandler);
          gokwikModalClosedHandler = async () => {
            await removeBundleItemsAddedSince(snapshotKeys);
            if (preExistingItems.length > 0) {
              await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: preExistingItems }),
              });
            }
          };
          window.gokwikSdk?.on?.('modal_closed', gokwikModalClosedHandler);
          gokwikBtn.click();
        } else {
          window.location.href = '/checkout';
        }
      } else {
        isCheckingOut = false;
        addToCartBtn.disabled = false;
        btnLabel.textContent = 'CHECKOUT |';
        showByobError(res.data?.message || 'Could not proceed. Please try again.');
      }
    });

    // ─── Slots collapse / expand ──────────────────────────────────────────────

    collapseBtn.addEventListener('click', () => {
      const h = slotsContainer.offsetHeight;
      manuallyCollapsed = true;
      slotsContainer.classList.add('is-collapsed');
      slotsFloat.classList.add('is-visible');
      drawerBody.scrollTop += h;
    });

    expandBtn.addEventListener('click', () => {
      manuallyCollapsed = false;
      slotsContainer.classList.remove('is-collapsed');
      slotsFloat.classList.remove('is-visible');
      drawerBody.scrollTop = 0;
    });

    renderScentGrid();
    refreshUI();
  };

  const boot = async () => {
    const drawerEl = document.getElementById('byobDrawer');
    if (!drawerEl) return;

    const OFFER_ID = drawerEl.dataset.offerId || '';
    const PREFILL_VARIANT_ID = drawerEl.dataset.prefillVariant || '';

    const sdk = await waitForSdk();
    if (!sdk) return;

    let instance;
    try {
      instance = await sdk.init({
        offerId: OFFER_ID,
        initFetch: (steps) => {
          const map = {};
          for (const id of Object.keys(steps)) { map[id] = 'all'; }
          return map;
        },
      });
    } catch {
      for (const btn of document.querySelectorAll('.js-open-byob-drawer')) { btn.classList.add('is-hidden'); }
      return;
    }

    if (!instance) {
      for (const btn of document.querySelectorAll('.js-open-byob-drawer')) { btn.classList.add('is-hidden'); }
      return;
    }

    byobBoot(sdk, instance, PREFILL_VARIANT_ID);
  };

  boot();
});
